import React from 'react';
import PropTypes from 'prop-types';
import request from 'superagent';
import FormData from 'form-data';

import edap from '@homeaway/edap-wrapper';
import classNames from 'classnames';
import {SVGIcon} from '@homeaway/react-svg';
import {ATTACHMENT__16} from '@homeaway/svg-defs/lib/defs/ATTACHMENT__16';
import {SEND__16} from '@homeaway/svg-defs/lib/defs/SEND__16';
import {AlertDanger, AlertSuccess} from '@homeaway/react-alerts';
import {LoadingOverlay} from '@homeaway/react-loading-overlay';
import {Button, ButtonIconPrimary} from '@homeaway/react-buttons';
import {FormTextArea} from '@homeaway/react-form-components';

import {Attachments} from './Attachments/Attachments';
import {ProtectPaymentAlert} from '../ProtectPaymentAlert/ProtectPaymentAlert';
import StayContext from '../../context/StayContext';
import {StayConsumer} from '../../context/StayContext';

import {
    tripSendMessageSelected,
    tripSendMessageSucceeded,
    TRIP_MESSAGES,
} from '../../../utils/EDAP/ConversationMessages';
import translate from '../../../utils/translations';
import {getReservationUuidFromStayContextData} from '../../../utils/stayDomain.js';
import {FallbackUI} from '../../molecules/FallbackUI/FallbackUI';
import {withErrorBoundary} from '../../ErrorBoundary/ErrorBoundary';

import './MessageForm.less';

class MessageForm extends React.Component {
    static propTypes = {
        conversationId: PropTypes.string,
        success: PropTypes.string,
        onSendDone: PropTypes.func,
        onSendFail: PropTypes.func,
        placeholder: PropTypes.string,
        systemData: PropTypes.shape({}),
        translations: PropTypes.shape({}),
    };

    static defaultProps = {
        isPM: false,
        onSendDone: () => {},
        onSendFail: () => {},
        success: '',
        systemData: {},
        translations: {},
    };

    constructor(props) {
        super(props);
        this.messageBoxRef = React.createRef();
        this.state = {
            error: '',
            files: [],
            hasInput: false,
            isFocused: false,
            isLoading: false,
            success: props.success,
            value: '',
        };
    }

    componentDidMount() {
        // eslint-disable-next-line no-unused-expressions
        this.messageBoxRef?.current?.focus();
        this.reservationUUID = getReservationUuidFromStayContextData(
            this.stayContext
        );
    }

    static contextType = StayContext;

    handleChange = (event) => {
        this.setState({
            hasInput: event.target.value.length > 0,
            value: event.target.value,
        });
    };

    get stayContext() {
        if (!this.context) {
            throw new Error(
                'stayContext must be used within a StayContextProvider'
            );
        }
        return this.context;
    }

    messageOwner = async (event) => {
        let data = new FormData();
        const {files, value} = this.state;
        const {conversationId, systemData} = this.props;

        tripSendMessageSelected({
            actionLocation: TRIP_MESSAGES,
            reservationUUID: this.reservationUUID,
        });
        event.preventDefault();

        if (value || files.length) {
            this.setState({
                error: '',
                isLoading: true,
                success: '',
            });
            data.append('messageText', value);
            data.append('conversationId', conversationId);

            files.forEach((attachment) => {
                data.append('attachment', attachment.file);
            });

            request
                .post(
                    `/bizops/travelerHome/messageOwnerWithAttachment?locale=${systemData.fullLocale}`
                )
                .set('X-HomeAway-Restfully', true)
                .send(data)
                .on('error', this.onSendFail)
                .then((response) => {
                    if (response.body && !response.body.violations) {
                        this.onSendDone(response);
                    } else {
                        this.onSendFail(response);
                    }
                });
        }
    };

    onSendFail = (response = {}) => {
        edap().push({
            name: 'generic.error',
            data: {
                logmessage: 'th error sending inbox message',
            },
        });

        this.props.onSendFail(response);

        this.setState({
            error: translate(
                this.props.translations,
                'trav-dash/trav-home',
                'messages',
                'th.inbox.conversation.reply.error'
            ),
            isLoading: false,
        });
    };

    onSendDone = (response = {}) => {
        tripSendMessageSucceeded({
            actionLocation: TRIP_MESSAGES,
            reservationUUID: this.reservationUUID,
        });

        this.props.onSendDone(response.body);
        this.setState({
            files: [],
            hasInput: false,
            isLoading: false,
            success: translate(
                this.props.translations,
                'trav-dash/trav-home',
                'messages',
                'th.inbox.conversation.reply.success'
            ),
            value: '',
        });
    };

    onChangeFiles = (event) => {
        const files = event.target.files || [];
        this.attach(files);
        event.target.value = '';
    };

    onDrop = (event) => {
        const dataTransfer =
            event.dataTransfer || event.originalEvent.dataTransfer;
        const files = dataTransfer.files || [];

        event.preventDefault();

        this.attach(files);
    };

    onBlurReplyField = () => {
        this.setState({
            isFocused: false,
        });
    };

    onFocusReplyField = () => {
        this.setState({
            isFocused: true,
        });
    };

    attach = (files) => {
        const {translations} = this.props;
        const numberFiles = files.length;
        let attachedFiles = this.state.files;
        let index;
        let file;
        let fileExtension;
        let fileName;

        for (index = 0; index < numberFiles; index++) {
            file = files[index];
            fileName = file.name;
            fileExtension = this.getFileExtension(fileName);

            if (!!file.type && !this.isAllowedFileType(file.type)) {
                // file.type for pdf is empty in case of IE 11
                this.setState({
                    error: translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        'th.inbox.conversation.reply.error.invalidFileType',
                        {
                            0: fileExtension,
                        }
                    ),
                });
            } else if (!this.isAllowedFileSize(file.size)) {
                this.setState({
                    error: translate(
                        translations,
                        'trav-dash/trav-home',
                        'messages',
                        'th.inbox.conversation.reply.error.tooLargeAttachment',
                        {
                            0: fileName,
                        }
                    ),
                });
            } else {
                attachedFiles.push({
                    error: false,
                    file,
                    name: fileName,
                });
            }
        }

        this.setState({
            files: [...attachedFiles],
        });
    };

    getFileExtension = (fileName) => {
        const extensionIndex = fileName.lastIndexOf('.');

        return extensionIndex > 0 ? fileName.substr(extensionIndex) : '';
    };

    isAllowedFileSize = (fileSize) => {
        return fileSize > 0 && fileSize <= 10 * 1000000; // 10 MB
    };

    isAllowedFileType = (fileType) => {
        return (
            /^application\/pdf/.test(fileType) || // PDF
            /^image\/gif/.test(fileType) || // GIF
            /^image\/jpeg/.test(fileType) || // JPG
            /^image\/png/.test(fileType)
        ); // PNG
    };

    removeAttachment = (id) => {
        let files = this.state.files;

        files.splice(id, 1);
        this.setState({
            files: [...files],
        });
    };

    render() {
        const {
            placeholder,
            systemData: {isMobile},
            translations,
        } = this.props;
        const {isFocused, hasInput, files, isLoading, success, value, error} =
            this.state;
        const formClass = classNames('form-control MessageForm__input', {
            'MessageForm__input--focused': isFocused,
        });
        const btnXsClass = classNames(
            'btn-primary hidden-lg hidden-md hidden-sm MessageForm__send-btn-xs',
            {'MessageForm__send-btn-xs--visible': hasInput || files.length > 0}
        );

        return (
            <div className="MessageForm">
                <div className="hidden-xs">
                    <div>
                        <h3>
                            {translate(
                                translations,
                                'trav-dash/trav-home',
                                'messages',
                                'th.inbox.conversation.sendamessageto.host'
                            )}
                        </h3>
                        <p>
                            {translate(
                                translations,
                                'trav-dash/trav-home',
                                'messages',
                                'th.inbox.conversation.haveaquestion.host'
                            )}
                        </p>
                        {!isMobile && (
                            <StayConsumer>
                                {({stay}) => {
                                    const {
                                        unit: {integratedPropertyManager},
                                    } = stay;
                                    return (
                                        !integratedPropertyManager && (
                                            <ProtectPaymentAlert
                                                translations={translations}
                                            />
                                        )
                                    );
                                }}
                            </StayConsumer>
                        )}
                    </div>
                </div>
                <div className="MessageForm__form" onDrop={this.onDrop}>
                    <LoadingOverlay loading={isLoading} />
                    <form role="form">
                        {error && <AlertDanger msg={error} />}

                        {success && <AlertSuccess msg={success} />}

                        <div className="form-group MessageForm__form-group">
                            <label
                                className="btn btn-link MessageForm__btn-attach--xs visible-xs pull-left"
                                title={translate(
                                    translations,
                                    'trav-dash/trav-home',
                                    'messages',
                                    'th.button.add.attachment'
                                )}
                            >
                                <SVGIcon markup={ATTACHMENT__16} />
                                <input
                                    className="sr-only"
                                    onChange={this.onChangeFiles}
                                    type="file"
                                    accept="application/pdf,image/*"
                                    multiple
                                />
                            </label>

                            <div
                                className={formClass}
                                onBlur={this.onBlurReplyField}
                                onFocus={this.onFocusReplyField}
                            >
                                <FormTextArea
                                    className={'ghost-input'}
                                    id={'MessageForm__text-area'}
                                    label={placeholder}
                                    name={'description'}
                                    onChange={this.handleChange}
                                    ref={this.messageBoxRef}
                                    value={value}
                                />

                                <div className="MessageForm__footer">
                                    <div className="MessageForm__attachments">
                                        <Attachments
                                            attachments={files}
                                            onRemove={this.removeAttachment}
                                        />
                                    </div>
                                    <div className="MessageForm__actions">
                                        <label
                                            htmlFor="attachment-input"
                                            className="btn btn-default btn-icon-circle pull-right hidden-xs MessageForm__btn-attach"
                                            title={translate(
                                                translations,
                                                'trav-dash/trav-home',
                                                'messages',
                                                'th.button.add.attachment'
                                            )}
                                            type="button"
                                        >
                                            <input
                                                className="sr-only"
                                                onChange={this.onChangeFiles}
                                                name="attachment-input"
                                                id="attachment-input"
                                                type="file"
                                                accept="application/pdf,image/*"
                                                multiple
                                            />
                                            <SVGIcon markup={ATTACHMENT__16} />
                                        </label>
                                        <Button
                                            className="btn--nowrap pull-right hidden-xs"
                                            label={
                                                <React.Fragment>
                                                    {translate(
                                                        translations,
                                                        'trav-dash/trav-home',
                                                        'messages',
                                                        'th.button.send'
                                                    )}{' '}
                                                    <SVGIcon
                                                        inlineFlex
                                                        markup={SEND__16}
                                                    />
                                                </React.Fragment>
                                            }
                                            onClick={this.messageOwner}
                                        />
                                        <ButtonIconPrimary
                                            circle
                                            className={btnXsClass}
                                            label={translate(
                                                translations,
                                                'trav-dash/trav-home',
                                                'messages',
                                                'th.button.send'
                                            )}
                                            onClick={this.messageOwner}
                                            svgMarkup={SEND__16}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        );
    }
}

const MessageFormWithErrorBoundary = (props) => {
    const {translations} = props;
    const ERROR_BOUNDARY_TEXT = translate(
        translations,
        'trav-dash/trav-home',
        'messages',
        'error.boundary.message'
    );
    const ERROR_BOUNDARY_TITLE = translate(
        translations,
        'trav-dash/trav-home',
        'messages',
        'th.inbox.conversation.sendamessageto.pm'
    );

    return withErrorBoundary(
        <MessageForm {...props} />,
        'message-form',
        <FallbackUI
            errorText={ERROR_BOUNDARY_TEXT}
            title={ERROR_BOUNDARY_TITLE}
        />
    );
};
export {MessageFormWithErrorBoundary as MessageForm};
